import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "technical-training",
  kind: "horizontal",
  title: "Technical Training",
  shortTitle: "Training",
  status: "draft",
  tier: "we-do",
  seoTitle: "Technical Training | General Applications",
  seoDescription:
    "Hands-on technical training and workshops for software, data, AI, cybersecurity, controls, and operational systems.",
  summary:
    "Technical instruction and workshops built around the systems, decisions, and failure modes people must understand to do the work well.",
  image: "/images/blog/reading-a-data-module-code-hero.webp",
  relatedCapabilitySlugs: ["automation-operations"],
  relatedIndustrySlugs: ["energy", "enterprise", "military"],
  engineering: [
    "Role-specific technical workshops grounded in actual systems and operating decisions",
    "Software architecture, data, search, and workflow-engineering instruction",
    "AI system boundaries, validation, and human-review practices",
    "Cybersecurity, controls, compliance, and operational-technology concepts",
    "Technical documentation, procedural systems, and implementation handoff training",
    "Exercises, examples, and reference materials adapted to the audience's work",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Teach the Work, Not Just the Vocabulary",
    },
    {
      type: "paragraph",
      text: "Technical training is most useful when it starts with the decisions people have to make: what to trust, what to verify, how a system fails, and when to stop and ask for help. The material should make those decisions clearer in the context where they will be used, rather than treating a tool or framework as the whole subject.",
    },
    {
      type: "heading",
      level: 2,
      text: "Instruction That Leaves Something Behind",
    },
    {
      type: "paragraph",
      text: "A session can introduce a topic, but lasting training leaves the team with shared language, worked examples, practical reference material, and a way to recognize the next exception. That makes it useful for engineers, operators, technical leaders, and cross-functional teams working on the same system from different perspectives.",
    },
  ],
  phases: {
    study: {
      focus:
        "Identify the audience, the work they perform, the decisions that cause friction, and the technical material that will make a practical difference.",
      produces: [
        "Audience, role, and prior-knowledge map",
        "Operational-task and decision inventory",
        "Training objectives grounded in real work",
        "Session, workshop, and reference-material plan",
      ],
      fit: {
        good: [
          "Teams adopting a system, method, or technical responsibility that needs shared judgment rather than a product walkthrough",
          "Organizations that need engineers, operators, and stakeholders to reason from the same technical model",
        ],
        poor: [
          "A request for an accredited certification program or a generic lecture with no defined audience or working context",
          "A request to substitute training for needed system access, documentation, or operational ownership",
        ],
      },
    },
    "initial-design": {
      focus:
        "Shape a learning path around the audience's decisions, examples, and likely failure modes instead of a product feature list.",
      produces: [
        "Role-specific curriculum and session sequence",
        "Worked examples and scenario design",
        "Shared vocabulary and reference-material outline",
        "Facilitation and participation plan",
      ],
    },
    "detailed-design": {
      focus:
        "Prepare the exercises, demonstrations, and supporting artifacts that let participants apply the material to their own work.",
      produces: [
        "Workshop materials and technical demonstrations",
        "Exercises tied to real decisions and exceptions",
        "Reference guides, checklists, and follow-up prompts",
        "Feedback and comprehension-review approach",
      ],
    },
    implementation: {
      focus:
        "Deliver the sessions, adapt discussion to the questions that emerge, and hand over the material needed for continued use.",
      produces: [
        "Facilitated technical sessions or workshops",
        "Shared reference materials and worked examples",
        "Captured questions, gaps, and follow-up actions",
        "Recommendations for continued practice or documentation",
      ],
    },
  },
};
